import React, { useState } from "react";
import {
  alpha,
  Chip,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
  Divider,
  Fade,
} from "@mui/material";
import {
  MoneyOff as ExpenseIcon,
  AttachMoney as IncomeIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { CategoryType } from "../../models/finance/CategoryType";
import Category from "../../models/finance/Category";
import CategoryEditDialogComponent from "./CategoryEditDialogComponent";
import CategoryItem from "./CategoryItem";
import AddCategoryFormComponent from "./AddCategoryFormComponent";
import SurfaceCard from "../common/ui/SurfaceCard";

interface CategoryListProps {
  categoryType: CategoryType;
  categories: Category[];
  onAddCategory: (title: string, type: CategoryType) => void;
  onDeleteCategory: (category: Category, fallbackId?: string) => void;
  onUpdateCategory: (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => void;
  getFinanceItemsNumber: (
    categoryType: CategoryType,
    categoryId: string
  ) => Promise<number>;
  addError?: string | null;
  onClearAddError?: () => void;
}

const CategoryListComponent: React.FC<CategoryListProps> = ({
  categoryType,
  categories,
  onAddCategory,
  onDeleteCategory,
  onUpdateCategory,
  getFinanceItemsNumber,
  addError,
  onClearAddError,
}) => {
  const theme = useTheme();
  const [showInactive, setShowInactive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  const handleOpenDialog = (category: Category) => {
    setSelectedCategory({ ...category });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const activeCategories = categories
    .filter((cat) => cat.isActive)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  const inactiveCategories = categories
    .filter((cat) => !cat.isActive)
    .sort((a, b) => {
      const dateA = new Date(a.deactivatedAt || 0).getTime();
      const dateB = new Date(b.deactivatedAt || 0).getTime();
      return dateB - dateA;
    });

  const normalizedSearch = searchText.trim().toLowerCase();
  const searchedActiveCategories = activeCategories.filter((cat) =>
    normalizedSearch ? cat.title.toLowerCase().includes(normalizedSearch) : true
  );
  const searchedInactiveCategories = inactiveCategories.filter((cat) =>
    normalizedSearch ? cat.title.toLowerCase().includes(normalizedSearch) : true
  );

  const filteredCategories = showInactive
    ? [...searchedActiveCategories, ...searchedInactiveCategories]
    : searchedActiveCategories;

  const isExpense = categoryType === CategoryType.Expense;
  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  const headerTint = alpha(themeColor.main, 0.08);
  const borderTint = alpha(themeColor.main, 0.28);
  const icon = isExpense ? <ExpenseIcon /> : <IncomeIcon />;

  return (
    <Fade in={true} timeout={800}>
      <SurfaceCard
        sx={{
          height: 'fit-content',
          borderRadius: "18px",
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(140deg, ${headerTint}, transparent)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.8 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 2,
                background: alpha(themeColor.main, 0.18),
                color: themeColor.light,
                border: `1px solid ${borderTint}`,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.light }}>
                {isExpense ? "Expense" : "Income"} Categories
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.8 }}>
            <Chip size="small" label={`${activeCategories.length} active`} sx={{ borderRadius: "10px" }} />
            <Chip size="small" label={`${inactiveCategories.length} inactive`} variant="outlined" sx={{ borderRadius: "10px" }} />
            {normalizedSearch && (
              <Chip
                size="small"
                label={`${filteredCategories.length} matching`}
                sx={{
                  borderRadius: "10px",
                  bgcolor: alpha(themeColor.main, 0.16),
                  color: themeColor.light,
                }}
              />
            )}
          </Stack>

          <TextField
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            size="small"
            fullWidth
            placeholder={`Search ${isExpense ? "expense" : "income"} categories`}
            sx={{ mb: 1.6 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Add Category Form */}
          <AddCategoryFormComponent
            onAdd={(name) => onAddCategory(name, categoryType)}
            showInactive={showInactive}
            onToggleShowInactive={() => setShowInactive(!showInactive)}
            error={addError}
            onClearError={onClearAddError}
            typeLabel={isExpense ? "Expense" : "Income"}
          />
        </Box>

        {/* Categories Content */}
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {filteredCategories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: theme.palette.grey[100],
                  color: theme.palette.grey[400],
                  mb: 2,
                }}
              >
                {icon}
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No {isExpense ? "expense" : "income"} categories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showInactive
                  ? "No categories match your current filters."
                  : "No active categories. Create one above or toggle to show inactive categories."
                }
              </Typography>
            </Box>
          ) : (
            <>
              {/* Active Categories */}
              {searchedActiveCategories.length > 0 && (
                <Box sx={{ mb: searchedActiveCategories.length > 0 && searchedInactiveCategories.length > 0 && showInactive ? 2.25 : 0 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Active Categories ({searchedActiveCategories.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.1,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.52),
                    }}
                  >
                    {searchedActiveCategories.map((cat) => (
                      <CategoryItem
                        key={cat.id}
                        category={cat}
                        onClick={() => handleOpenDialog(cat)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Inactive Categories (if showing) */}
              {showInactive && searchedInactiveCategories.length > 0 && (
                <>
                  {searchedActiveCategories.length > 0 && <Divider sx={{ my: 2.25 }} />}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      Inactive Categories ({searchedInactiveCategories.length})
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.1,
                        p: 1.5,
                        borderRadius: "12px",
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.42),
                      }}
                    >
                      {searchedInactiveCategories.map((cat) => (
                        <CategoryItem
                          key={cat.id}
                          category={cat}
                          onClick={() => handleOpenDialog(cat)}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>

        {/* Edit Dialog */}
        {selectedCategory && (
          <CategoryEditDialogComponent
            open={dialogOpen}
            category={selectedCategory}
            categories={categories}
            categoryType={categoryType}
            onClose={handleCloseDialog}
            onDelete={onDeleteCategory}
            onUpdate={onUpdateCategory}
            getFinanceItemsNumber={getFinanceItemsNumber}
          />
        )}
      </SurfaceCard>
    </Fade>
  );
};

export default CategoryListComponent;
